import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import {
  HomeIcon,
  CogIcon,
  WrenchScrewdriverIcon,
  ClipboardDocumentCheckIcon,
  ExclamationTriangleIcon,
  UsersIcon,
  BuildingOfficeIcon,
  FolderIcon,
  Bars3Icon,
  XMarkIcon,
  ArrowRightOnRectangleIcon,
  ChevronDownIcon,
  CubeIcon,
  QrCodeIcon,
  DocumentChartBarIcon,
  TruckIcon,
  ShoppingCartIcon,
  ClipboardDocumentListIcon,
  QuestionMarkCircleIcon,
  BeakerIcon,
  CheckBadgeIcon,
  WrenchIcon,
  ArrowsRightLeftIcon,
  CubeTransparentIcon,
  BoltIcon,
  WifiIcon,
  ShieldExclamationIcon
} from '@heroicons/react/24/outline';

const Layout = ({ children }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [purchasingOpen, setPurchasingOpen] = useState(false);
  const [settingsMenuOpen, setSettingsMenuOpen] = useState(false);
  const { user, logout, isAdmin, isOgs } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const userMenuRef = useRef(null);

  // Sayfa değişince mobil sidebar kapansın
  useEffect(() => {
    setSidebarOpen(false);
  }, [location.pathname]);

  // Kullanıcı menüsünü dışarı tıklayınca kapat
  useEffect(() => {
    const onDocClick = (e) => {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target)) {
        setUserMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const navigation = [
    { name: 'Dashboard',            href: '/',                       icon: HomeIcon },
    { name: 'Cihazlar',             href: '/devices',                icon: CogIcon },
    { name: 'Kaynak Makineleri',    href: '/welding-machines',       icon: BoltIcon },
    { name: 'Vinçler',              href: '/cranes',                 icon: ArrowsRightLeftIcon },
    { name: 'Kediler',              href: '/caddies',                icon: CubeTransparentIcon },
    { name: 'QR Tarayıcı',          href: '/qr-scanner',             icon: QrCodeIcon },
    { name: 'NFC Oku',              href: '/qr-scanner?mode=nfc',    icon: WifiIcon },
    { name: 'Bakımlar',             href: '/maintenances',           icon: WrenchScrewdriverIcon },
    { name: 'Bakım Onayları',       href: '/maintenance-approvals',  icon: CheckBadgeIcon },
    { name: 'Bakımlarım',           href: '/my-maintenances',        icon: WrenchIcon },
    { name: 'Periyodik Kontroller', href: '/periodic-controls',      icon: ClipboardDocumentCheckIcon },
    { name: 'Arıza/Talepler',       href: '/fault-reports',          icon: ExclamationTriangleIcon },
    { name: 'Yedek Parçalar',       href: '/spare-parts',            icon: CubeIcon },
    { name: 'Kalibrasyon',          href: '/calibration',            icon: BeakerIcon },
    { name: 'Raporlar',             href: '/reports',                icon: DocumentChartBarIcon },
  ];

  const purchasingNav = [
    { name: 'Tedarikçiler',         href: '/suppliers',         icon: TruckIcon },
    { name: 'Satınalma Talepleri',  href: '/purchase-requests', icon: ClipboardDocumentListIcon },
    { name: 'Siparişler',           href: '/purchase-orders',   icon: ShoppingCartIcon },
  ];

  const settingsNav = [
    { name: 'Kullanıcılar',         href: '/settings/users',       icon: UsersIcon,                 adminOnly: true },
    { name: 'Departmanlar',         href: '/settings/departments', icon: BuildingOfficeIcon,        adminOnly: true },
    { name: 'Cihaz Kategorileri',   href: '/settings/categories',  icon: FolderIcon,                adminOnly: true },
    { name: 'Kontrol Listeleri',    href: '/settings/checklists',  icon: ClipboardDocumentCheckIcon,adminOnly: true },
  ];

  const isActive = (href) => {
    if (href === '/') return location.pathname === '/';
    const [path, query] = href.split('?');
    if (query) {
      // Sorgu parametreli linkler tam eşleşmeyle aktif
      return location.pathname === path && location.search.includes(query);
    }
    // Sorgusuz link: alt yolu kapsasın ama farklı sorgu modunu aktif sayma
    if (location.pathname === path && location.search) return false;
    return location.pathname.startsWith(path);
  };

  // Tek bir nav linki — aktifte parlak accent çubuk + glow
  const NavItem = ({ item }) => {
    const active = isActive(item.href);
    return (
      <Link
        to={item.href}
        className={`group relative flex items-center gap-3 px-3 py-2.5 rounded-lg mb-1 text-sm font-medium transition-all
          ${active
            ? 'bg-white/15 text-white shadow-glow-soft'
            : 'text-white/70 hover:text-white hover:bg-white/10'}`}
      >
        {active && (
          <span className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-7 rounded-r bg-gradient-to-b from-accent-300 to-accent-500 shadow-glow"></span>
        )}
        <item.icon className={`h-5 w-5 transition ${active ? 'text-accent-300' : 'text-white/60 group-hover:text-white'}`} />
        <span className="truncate">{item.name}</span>
      </Link>
    );
  };

  const SectionHeader = ({ label, open, onToggle }) => (
    <button
      onClick={onToggle}
      className="mt-4 mb-1 w-full flex items-center justify-between px-3 py-2 text-[11px] font-semibold text-white/50 uppercase tracking-wider hover:text-white/80 transition"
    >
      {label}
      <ChevronDownIcon className={`h-3.5 w-3.5 transition-transform ${open ? 'rotate-180' : ''}`} />
    </button>
  );

  // Sidebar içeriği — mobile/desktop ortak
  const SidebarContent = () => (
    <>
      {/* Logo */}
      <div className="flex items-center gap-3 h-16 px-5 border-b border-white/10">
        <div className="w-9 h-9 rounded-lg bg-white/95 p-1 shadow-glow-soft flex items-center justify-center">
          <img src="/mim_logo.svg" alt="MİM" className="w-full h-full object-contain"
            onError={(e) => { e.target.style.display = 'none'; }} />
        </div>
        <div className="min-w-0">
          <p className="text-white font-bold leading-tight">Bakım-Onarım</p>
          <p className="text-[11px] text-white/60 leading-tight">Cihaz Takip Sistemi</p>
        </div>
      </div>

      <nav className="px-3 py-4 overflow-y-auto" style={{ height: 'calc(100vh - 64px)' }}>
        {navigation.map((item) => <NavItem key={item.name} item={item} />)}

        <SectionHeader label="Satınalma" open={purchasingOpen} onToggle={() => setPurchasingOpen(!purchasingOpen)} />
        {purchasingOpen && purchasingNav.map((item) => <NavItem key={item.name} item={item} />)}

        {isAdmin && (
          <>
            <SectionHeader label="Ayarlar" open={settingsMenuOpen} onToggle={() => setSettingsMenuOpen(!settingsMenuOpen)} />
            {settingsMenuOpen && settingsNav.map((item) => <NavItem key={item.name} item={item} />)}
          </>
        )}

        {isOgs && (
          <NavItem item={{ name: 'Silme Logları', href: '/deletion-logs', icon: ShieldExclamationIcon }} />
        )}

        <div className="h-6"></div>
      </nav>
    </>
  );

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Mobile sidebar */}
      <div className={`fixed inset-0 z-50 lg:hidden ${sidebarOpen ? '' : 'pointer-events-none'}`}>
        <div
          className={`fixed inset-0 bg-slate-900/60 backdrop-blur-sm transition-opacity ${sidebarOpen ? 'opacity-100' : 'opacity-0'}`}
          onClick={() => setSidebarOpen(false)}
        />
        <div className={`fixed top-0 bottom-0 left-0 w-72 bg-gradient-to-b from-primary-800 via-primary-900 to-slate-900 shadow-2xl transition-transform ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}`}>
          <button
            onClick={() => setSidebarOpen(false)}
            className="absolute top-4 right-4 text-white/70 hover:text-white p-1"
            aria-label="Kapat"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
          <SidebarContent />
        </div>
      </div>

      {/* Desktop sidebar */}
      <aside className="hidden lg:fixed lg:inset-y-0 lg:flex lg:flex-col lg:w-64 bg-gradient-to-b from-primary-800 via-primary-900 to-slate-900 shadow-xl">
        <SidebarContent />
      </aside>

      {/* Main */}
      <div className="lg:pl-64 flex flex-col min-h-screen">
        {/* Top bar */}
        <header className="sticky top-0 z-40 bg-white/80 backdrop-blur-md border-b border-gray-200/80 shadow-sm">
          <div className="flex h-16 items-center gap-3 px-4 lg:px-8">
            <button
              className="lg:hidden p-2 -ml-2 rounded-lg hover:bg-gray-100 text-gray-600"
              onClick={() => setSidebarOpen(true)}
              aria-label="Menüyü aç"
            >
              <Bars3Icon className="h-6 w-6" />
            </button>

            <div className="flex-1" />

            {/* Yardım */}
            <a
              href="/KULLANIM_KILAVUZU.html"
              target="_blank"
              rel="noopener noreferrer"
              className="hidden sm:inline-flex items-center gap-2 px-3 py-2 text-sm text-gray-600 hover:text-primary-600 hover:bg-primary-50 rounded-lg transition"
              title="Kullanım Kılavuzu"
            >
              <QuestionMarkCircleIcon className="h-5 w-5" />
              <span className="hidden md:inline">Yardım</span>
            </a>

            {/* User menu */}
            <div className="relative" ref={userMenuRef}>
              <button
                className="flex items-center gap-2 p-1.5 pr-3 rounded-full hover:bg-gray-100 transition"
                onClick={() => setUserMenuOpen(!userMenuOpen)}
              >
                <div className="h-9 w-9 rounded-full bg-gradient-primary flex items-center justify-center shadow-glow-soft text-white font-semibold">
                  {user?.fullName?.charAt(0)?.toUpperCase() || 'U'}
                </div>
                <span className="hidden md:block text-sm font-medium text-gray-700">{user?.fullName}</span>
                <ChevronDownIcon className={`h-4 w-4 text-gray-500 transition-transform ${userMenuOpen ? 'rotate-180' : ''}`} />
              </button>

              {userMenuOpen && (
                <div className="absolute right-0 mt-2 w-64 bg-white rounded-xl shadow-card-hover border border-gray-100 py-1 animate-fade-in overflow-hidden">
                  <div className="px-4 py-3 bg-gradient-to-br from-primary-50 to-accent-50 border-b border-gray-100">
                    <p className="text-sm font-semibold text-gray-800 truncate">{user?.fullName}</p>
                    <p className="text-xs text-gray-500 truncate">{user?.email}</p>
                    {user?.role && (
                      <span className="inline-block mt-2 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide bg-primary-100 text-primary-700 rounded">
                        {user.role}
                      </span>
                    )}
                  </div>
                  <button
                    onClick={handleLogout}
                    className="w-full flex items-center gap-2 px-4 py-2.5 text-sm text-danger-600 hover:bg-danger-50 transition"
                  >
                    <ArrowRightOnRectangleIcon className="h-5 w-5" />
                    Çıkış Yap
                  </button>
                </div>
              )}
            </div>
          </div>
        </header>

        {/* Page content */}
        <main className="flex-1 p-4 lg:p-8 animate-fade-in">
          {children}
        </main>
      </div>
    </div>
  );
};

export default Layout;
